import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Typography,
    Box,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import Datepicker from "../../date-filed/DatePicker";
import { StyledButton, StyledCancelButton } from "../../StyledComponent";
import CancleEnrollment from "./CancleEnrollment";
import { numberRegex } from "../../../regex/regex";

const EnrollProgramDialog = ({
    open,
    onClose,
    onSubmit,
    programId,
    mainId,
    existingProgramData,
    allPrograms,
    onCancelSuccess,
}) => {
    const [program, setProgram] = useState(null);
    const [programDate, setProgramDate] = useState(
         new Date().toISOString().split("T")[0]
    );
    const [paymentDueDate, setPaymentDueDate] = useState(existingProgramData?.invoice?.dueDate || null);
    const [showDiscountFields, setShowDiscountFields] = useState(!!existingProgramData?.invoice?.feeDiscount);
    const [feeDiscount, setFeeDiscount] = useState(existingProgramData?.invoice?.feeDiscount);
    const [feeDiscountNote, setFeeDiscountNote] = useState(existingProgramData?.invoice?.discountNote);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [errors, setErrors] = useState({ program: false, programDate: false, paymentDueDate: false });
    const [loading, setLoading] = useState(false);
    const isSubmittingRef = useRef(false);
    const isEditMode = !!programId;

    useEffect(() => {
        if (open) {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    }, [open]);


    useEffect(() => {
        
        if (existingProgramData) {
            setProgram(existingProgramData?.purchaseProgram || null);
            setProgramDate(existingProgramData.invoice?.createdAt || new Date().toISOString().split("T")[0]);
            setPaymentDueDate(existingProgramData.invoice?.dueDate || null);
            setShowDiscountFields(!!existingProgramData.invoice?.feeDiscount);
            setFeeDiscount(existingProgramData.invoice?.feeDiscount || "");
            setFeeDiscountNote(existingProgramData.invoice?.discountNote || "");
        }
    }, [existingProgramData]);
    
    

    const handleDiscountButtonClick = () => setShowDiscountFields(prev => !prev);
    const handleCancelEnrollmentClick = () => setOpenConfirmDialog(true);
    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
        onClose();
    };


    const invoiceId = isEditMode ? existingProgramData?.invoiceId || null : null;
    const existId = isEditMode ? existingProgramData?.id || null : null;

    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;
        let hasErrors = false;
        const newErrors = { program: false, programDate: false, paymentDueDate: false };

        if (!isEditMode && !program) {
            newErrors.program = true;
            hasErrors = true;
        }
        if (!programDate) {
            newErrors.programDate = true;
            hasErrors = true;
        }
        if (!paymentDueDate) {
            newErrors.paymentDueDate = true;
            hasErrors = true;
        }

        if (hasErrors) {
            setErrors(newErrors);
            return;
        }

        isSubmittingRef.current = true;
        setLoading(true);
        try {
            await onSubmit({
                program,
                programDate,
                paymentDueDate,
                discount: feeDiscount,
                discountNote: feeDiscountNote,
                invoiceId,
                existId,
            });
            onClose();
        } catch (error) {
            console.error("Submission error:", error);
            isSubmittingRef.current = false;
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle fontWeight="bold">
                {isEditMode ? "Edit Program" : "Add Program"}
            </DialogTitle>
            <DialogContent>
                {/* Program Information */}
                {isEditMode && (
                   <Grid item xs={12}>
                   <TextField
                       id="program-title"
                       variant="standard"
                       fullWidth
                       defaultValue={existingProgramData?.invoice?.purchaseProgram?.title || "Program Title"}
                       InputProps={{
                        readOnly: true,
                    }}
                   />
                   {existingProgramData?.invoice?.purchaseProgram?.description && (
                       <Typography variant="body2" marginTop={4}>
                           {existingProgramData?.invoice?.purchaseProgram?.description}
                       </Typography>
                   )}
               
                   {existingProgramData?.invoice?.purchaseProgram?.price && (
                       <Typography
                           variant="body1"
                           color="secondary"
                           marginTop={2}
                           display="flex"
                           justifyContent="flex-end"
                       >
                           ₹ {existingProgramData?.invoice?.purchaseProgram?.price}
                       </Typography>
                   )}
               </Grid>
               
                )}

                {/* Program Selection */}
                {!isEditMode && (
                    <Autocomplete
                        id="choose-program"
                        options={allPrograms}

                        getOptionLabel={(option) => option.title || ""}
                        value={program}
                        onChange={(event, newValue) => {
                            setProgram(newValue);
                            if (errors.program) setErrors(prev => ({ ...prev, program: false }));
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Choose Program"
                                variant="standard"
                                required
                                error={errors.program}
                                helperText={errors.program ? "Please select a program" : ""}
                            />
                        )}

                    />
                )}

                {program?.description && (
                    <Typography variant="body2" marginTop={4}>
                        {program.description}
                    </Typography>
                )}

                {program?.price && (
                    <Typography
                        variant="body1"
                        color="secondary"
                        marginTop={2}
                        display="flex"
                        justifyContent="flex-end"
                    >
                        ₹ {program?.price}
                    </Typography>
                )}

                {/* Date Fields */}
                <Box display="flex" gap={2} className="w-full mt-4">
                    <Datepicker
                        value={programDate}
                        onChange={(val) => {
                            setProgramDate(val);
                            if (errors.programDate) setErrors(prev => ({ ...prev, programDate: false }));
                        }}
                        label="Program Date"
                        required
                        error={errors.programDate}
                        helperText={errors.programDate ? "Program date is required" : ""}
                        minDate={new Date().toISOString().split('T')[0]}
                    />
                    <Datepicker
                        value={paymentDueDate}
                        onChange={(val) => {
                            setPaymentDueDate(val);
                            if (errors.paymentDueDate) setErrors(prev => ({ ...prev, paymentDueDate: false }));
                        }}
                        label="Payment Due Date"
                        required
                        error={errors.paymentDueDate}
                        helperText={errors.paymentDueDate ? "Payment due date is required" : ""}
                        minDate={new Date().toISOString().split('T')[0]}
                    />
                </Box>

                {/* Discount Fields */}
                <Box display="flex" alignItems="center" marginTop={2}>
                    <Typography
                        variant="body2"
                        color={showDiscountFields ? "error" : "secondary"}
                        style={{ cursor: "pointer" }}
                        onClick={handleDiscountButtonClick}
                    >
                        {showDiscountFields ? <RemoveIcon color="error" /> : <AddIcon color="secondary" />} Discount
                    </Typography>
                </Box>

                {/* {showDiscountFields && (
                    <Box display="flex" gap={2} className="w-full">
                        <TextField
                            id="fee-discount"
                            label="Fee Discount (in rupees)"
                            variant="standard"
                            fullWidth
                            value={feeDiscount}
                            onChange={(e) => setFeeDiscount(e.target.value)}
                            error={   
                                (!numberRegex.test(feeDiscount) && feeDiscount !== "") || 
                               
                                (isEditMode ? 
                                    (existingProgramData?.invoice?.purchaseProgram?.price && parseFloat(feeDiscount) > parseFloat(existingProgramData.invoice.purchaseProgram.price)) : 
                                    (program?.price && parseFloat(feeDiscount) > parseFloat(program.price))
                                )
                            }
                            helperText={
                                // Show helper text based on the conditions
                                !numberRegex.test(feeDiscount) && feeDiscount !== ""
                                    ? "Only numeric values are allowed."
                                    : (isEditMode ? 
                                        (existingProgramData?.invoice?.purchaseProgram?.price && parseFloat(feeDiscount) > parseFloat(existingProgramData.invoice.purchaseProgram.price)) : 
                                        (program?.price && parseFloat(feeDiscount) > parseFloat(program.price))
                                    )
                                    ? "Discount price should not be more than program price!"
                                    : ""
                            }
                            
                        />
                        <TextField
                            id="discount-note"
                            label="Discount Note"
                            variant="standard"
                            fullWidth
                            value={feeDiscountNote}
                            onChange={(e) => setFeeDiscountNote(e.target.value)}
                            error={!StringWithSpace.test(feeDiscountNote) && feeDiscountNote !== ""}
                            helperText={
                                !StringWithSpace.test(feeDiscountNote) && feeDiscountNote !== "" ? "Only letters are allowed." : ""
                            }
                        />
                    </Box>
                )} */}
                {showDiscountFields && (
    <Box display="flex" gap={2} className="w-full">
        <TextField
            id="fee-discount"
            label="Fee Discount (in rupees)"
            variant="standard"
            fullWidth
            value={feeDiscount}
            onChange={(e) => setFeeDiscount(e.target.value)}
            error={
                feeDiscount && 
                (!numberRegex.test(feeDiscount) || 
                (isEditMode 
                    ? existingProgramData?.invoice?.purchaseProgram?.price &&
                      parseFloat(feeDiscount) > parseFloat(existingProgramData.invoice.purchaseProgram.price)
                    : program?.price &&
                      parseFloat(feeDiscount) > parseFloat(program.price))
                )
            }
            helperText={
                feeDiscount &&
                (!numberRegex.test(feeDiscount)
                    ? "Only numeric values are allowed."
                    : (isEditMode 
                        ? existingProgramData?.invoice?.purchaseProgram?.price &&
                          parseFloat(feeDiscount) > parseFloat(existingProgramData.invoice.purchaseProgram.price)
                        : program?.price &&
                          parseFloat(feeDiscount) > parseFloat(program.price))
                    ? "Discount price should not be more than program price!"
                    : "")
            }
        />
        <TextField
            id="discount-note"
            label="Discount Note"
            variant="standard"
            fullWidth
            value={feeDiscountNote}
            onChange={(e) => setFeeDiscountNote(e.target.value)}
            // error={
            //     feeDiscountNote && !StringWithSpace.test(feeDiscountNote)
            // }
            // helperText={
            //     feeDiscountNote && !StringWithSpace.test(feeDiscountNote)
            //         ? "Only letters are allowed."
            //         : ""
            // }
        />
    </Box>
)}

            </DialogContent>

            <DialogActions style={{ display: "flex", justifyContent: "space-between" }}>
                {isEditMode && (
                    <StyledButton
                        onClick={handleCancelEnrollmentClick}
                        color="error"
                        variant="contained"
                        style={{ backgroundColor: "#d32f2f", color: "#fff" }}
                    >
                        Cancel Enrollment
                    </StyledButton>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', flex: 1, gap: 6 }}>
                    <StyledCancelButton onClick={onClose} color="default" variant="outlined">
                        Cancel
                    </StyledCancelButton>
                    <StyledButton
                        onClick={handleSubmit}
                        color="primary"
                        variant="contained"
                        // disabled={
                        //     !programDate || 
                        //     !paymentDueDate || 
                        //     (isEditMode && !programDate && !paymentDueDate) || 
                        //     (feeDiscount ? !numberRegex.test(feeDiscount):true) || 
                        //     (isEditMode ? 
                        //         (existingProgramData?.invoice?.purchaseProgram?.price && parseFloat(feeDiscount) > parseFloat(existingProgramData.invoice.purchaseProgram.price)) : 
                        //         (program?.price && parseFloat(feeDiscount) > parseFloat(program.price))
                        //     ) ||
                        //     feeDiscount === ""
                        // }
                        disabled={
                            loading ||
                            (feeDiscount && !numberRegex.test(feeDiscount)) || 
                            (isEditMode 
                                ? feeDiscount && 
                                  existingProgramData?.invoice?.purchaseProgram?.price &&
                                  parseFloat(feeDiscount) > parseFloat(existingProgramData.invoice.purchaseProgram.price)
                                : feeDiscount && 
                                  program?.price &&
                                  parseFloat(feeDiscount) > parseFloat(program.price)) 
                        }
                        
                    >
                        {loading ? 'Submitting...' : (isEditMode ? "Save" : "Submit")}
                    </StyledButton>

                </div>
            </DialogActions>
            <CancleEnrollment
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                mainId={mainId}
                onSuccess={onCancelSuccess}
            />
        </Dialog>
    );
};

export default EnrollProgramDialog;


